export type TicketOption = {
    id: number;
    name: string;
};

/** Matches the sentinel product_id used for POS custom orders in report SQL. */
export const CUSTOM_ORDER_TICKET_ID = 99999999999;
export const CUSTOM_ORDER_TICKET_NAME = 'Custom Order';

export function reportHasTicketNameColumn(columns: Array<{ id: string }> | undefined): boolean {
    return Boolean(columns?.some((column) => column.id === 'ticketName'));
}

export function toTicketId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        return value;
    }

    if (typeof value === 'string' && /^\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed > 0) {
            return parsed;
        }
    }

    return null;
}

export function getRowTicketName(row: Record<string, unknown>): string | null {
    const value = row.ticketName;
    return typeof value === 'string' && value.length > 0 ? value : null;
}

export function uniqueTicketNamesFromRows(rows: Array<Record<string, unknown>>): string[] {
    const names: string[] = [];
    const seen = new Set<string>();

    for (const row of rows) {
        const name = getRowTicketName(row);
        if (!name || seen.has(name)) {
            continue;
        }

        seen.add(name);
        names.push(name);
    }

    return names;
}

export function mergeTicketOptions(
    catalog: TicketOption[],
    rowNames: string[],
    selectedIds: number[] | undefined,
): TicketOption[] {
    const options: TicketOption[] = [];
    const seenIds = new Set<number>();
    const seenNames = new Set<string>();

    const add = (option: TicketOption) => {
        if (seenIds.has(option.id) || !option.name) {
            return;
        }

        seenIds.add(option.id);
        seenNames.add(option.name);
        options.push(option);
    };

    for (const option of catalog) {
        add(option);
    }

    for (const name of rowNames) {
        if (!name || seenNames.has(name)) {
            continue;
        }

        if (name === CUSTOM_ORDER_TICKET_NAME) {
            add({ id: CUSTOM_ORDER_TICKET_ID, name: CUSTOM_ORDER_TICKET_NAME });
        }
    }

    for (const id of selectedIds ?? []) {
        if (seenIds.has(id)) {
            continue;
        }

        add({
            id,
            name: id === CUSTOM_ORDER_TICKET_ID ? CUSTOM_ORDER_TICKET_NAME : `Ticket #${id}`,
        });
    }

    return options;
}

export function resolveTicketNamesToIds(
    ticketNames: string[] | undefined,
    ticketOptions: TicketOption[],
): number[] | undefined {
    if (ticketNames === undefined) {
        return undefined;
    }

    const idsByName = new Map(ticketOptions.map((option) => [option.name, option.id]));
    return ticketNames
        .map((name) => idsByName.get(name))
        .filter((id): id is number => typeof id === 'number');
}

/** `undefined` = all tickets. Otherwise keep rows whose current ticket name matches a selected id. */
export function filterRowsByTicketIds<TRow extends Record<string, unknown>>(
    rows: TRow[],
    ticketIds: number[] | undefined,
    ticketOptions: TicketOption[],
): TRow[] {
    if (ticketIds === undefined) {
        return rows;
    }

    const allowedIds = new Set(ticketIds);
    const allowedNames = new Set(
        ticketOptions.filter((option) => allowedIds.has(option.id)).map((option) => option.name),
    );

    return rows.filter((row) => {
        const name = getRowTicketName(row);
        return Boolean(name && allowedNames.has(name));
    });
}

export function areAllTicketsSelected(
    ticketOptions: TicketOption[],
    ticketIds: number[] | undefined,
): boolean {
    if (ticketIds === undefined) {
        return true;
    }

    if (ticketOptions.length === 0) {
        return ticketIds.length === 0;
    }

    return (
        ticketIds.length === ticketOptions.length &&
        ticketOptions.every((option) => ticketIds.includes(option.id))
    );
}
